import React, { useCallback, useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  getRestaurants,
  getEmployees,
  getEmployeeProfiles,
  getMonthlyAttendance,
} from "../Components/api/SalaryCalculationApi";

/* ═══════════════ FORMATTING HELPERS ═══════════════ */
const formatINR = (value) =>
  `₹${(Number(value) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

const minutesToHours = (minutes) => {
  const total = Math.round(Number(minutes) || 0);
  return `${Math.floor(total / 60)}h ${total % 60}m`;
};

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const f1 = (n) => +(Number(n) || 0).toFixed(1);

/* Overtime pay = overtime hours × hourly rate × this multiplier (1 = normal rate, 2 = double) */
const OVERTIME_MULTIPLIER = 1;

/* ═══════════════ SALARY CALCULATION (shared by screen + Excel) ═══════════════
   The API supplies raw data only; everything below is calculated here.

   Attendance credit
     salaryCalculationMode = "WorkingMinutes" -> workingMinutes ÷ expectedMinutesPerDay (max = workingDays)
     otherwise -> API attendanceCredit, else full + half×0.5 + quarter×0.25
   Leave credit  = min(leaveCreditAllowed, workingDays − attendance credit)
   Paid days     = attendance credit + leave credit (+ weekly-off/holiday days when weeklyOffPaid), max totalDays
   Overtime      = WorkingMinutes mode: minutes beyond expected × workingDays; otherwise API overtimeMinutes
═══════════════════════════════════════════════════════════════════════════ */
const calcSalary = (profile, att) => {
  const expectedPerDay = profile?.expectedWorkingMinutes || 0;
  const totalDays = att?.totalDaysInMonth || 0;
  const workingDays = att?.workingDays ?? totalDays;
  const expectedTotal = expectedPerDay * workingDays;
  const actual = att?.workingMinutes || 0;

  const byMinutes = att?.salaryCalculationMode === "WorkingMinutes" && expectedPerDay > 0;

  const attended = byMinutes
    ? Math.min(actual / expectedPerDay, workingDays)
    : att?.attendanceCredit ??
      (att?.fullDays || 0) + (att?.halfDays || 0) * 0.5 + (att?.quarterDays || 0) * 0.25;

  const leaveAllowed =
    att?.leaveCreditAllowed ?? att?.monthlyLeaveCredit ?? profile?.salary?.paidLeavePerMonth ?? 0;
  const leaveCredit = Math.min(leaveAllowed, Math.max(0, workingDays - attended));

  const offPaidDays = att?.weeklyOffPaid ? (att?.weeklyOffDays || 0) + (att?.holidayDays || 0) : 0;
  const paidDays = Math.min(totalDays, attended + leaveCredit + offPaidDays);
  const absent = Math.max(0, workingDays - attended);
  const leaveWithoutPay = Math.max(0, totalDays - paidDays);

  const isOT = profile?.isOvertimeAllowed === true;
  const overtimeMinutes = byMinutes
    ? Math.max(0, actual - expectedTotal)
    : att?.overtimeMinutes || 0;

  const s = profile?.salary || {};
  const basic = s.basic || 0;
  const perDay = totalDays > 0 ? basic / totalDays : 0;
  const gross = perDay * paidDays;
  const hourlyRate = expectedPerDay > 0 ? perDay / (expectedPerDay / 60) : 0;
  const overtimeSalary = isOT ? (overtimeMinutes / 60) * hourlyRate * OVERTIME_MULTIPLIER : 0;
  const deductions =
    (s.advanceDeduction || 0) + (s.loanEMI || 0) + (s.staffExp || 0) + (s.fineApplied || 0);

  return {
    expectedPerDay, workingDays, expectedTotal, actual, totalDays,
    attended, absent, leaveAllowed, leaveCredit, offPaidDays, paidDays, leaveWithoutPay,
    isOT, overtimeMinutes, basic, hourlyRate, gross, overtimeSalary, deductions,
    net: gross + overtimeSalary - deductions,
    efficiencyPct: expectedTotal > 0 ? (actual / expectedTotal) * 100 : null,
  };
};

/* ═══════════════ SMALL PRESENTATIONAL PIECES ═══════════════ */
const KpiCard = ({ label, value, accent = "slate", highlight = false, subtext }) => {
  const accents = {
    slate: "text-slate-900",
    emerald: "text-emerald-600",
    rose: "text-rose-600",
    teal: "text-teal-600",
    amber: "text-amber-600",
  };
  return (
    <div
      className={`rounded-xl bg-white p-4 shadow-sm ring-1 ${
        highlight ? "ring-teal-200 ring-2" : "ring-slate-200"
      }`}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1.5 text-xl font-bold tabular-nums ${accents[accent]}`}>{value}</p>
      {subtext && <p className="mt-0.5 text-xs text-slate-400">{subtext}</p>}
    </div>
  );
};

const AttendanceStat = ({ label, value, accent = "text-slate-900", subtext }) => (
  <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
    <p className="text-xs font-medium text-slate-500">{label}</p>
    <p className={`mt-0.5 text-base font-semibold tabular-nums ${accent}`}>{value}</p>
    {subtext && <p className="mt-0.5 text-[11px] text-slate-400">{subtext}</p>}
  </div>
);

const Skeleton = () => (
  <div className="animate-pulse space-y-4">
    <div className="h-24 rounded-xl bg-slate-200/70" />
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-20 rounded-xl bg-slate-200/70" />
      ))}
    </div>
    <div className="h-32 rounded-xl bg-slate-200/70" />
  </div>
);

const ErrorBanner = ({ message, onRetry }) => (
  <div className="flex items-start justify-between gap-4 rounded-xl border border-rose-200 bg-rose-50 p-4">
    <div>
      <p className="text-sm font-semibold text-rose-700">Something went wrong</p>
      <p className="mt-0.5 text-sm text-rose-600">{message}</p>
    </div>
    {onRetry && (
      <button
        onClick={onRetry}
        className="shrink-0 rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700"
      >
        Retry
      </button>
    )}
  </div>
);

const EmptyState = ({ title, description }) => (
  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-12 text-center">
    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-teal-50 text-teal-600">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    </div>
    <p className="text-sm font-semibold text-slate-700">{title}</p>
    <p className="mt-1 max-w-xs text-sm text-slate-500">{description}</p>
  </div>
);

/* ═══════════════ COMPONENT ═══════════════ */
const SalaryCalculation = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestId, setSelectedRestId] = useState("");

  const [employees, setEmployees] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [attendance, setAttendance] = useState([]);

  const [currentIndex, setCurrentIndex] = useState(0);

  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  const [restaurantsLoading, setRestaurantsLoading] = useState(true);
  const [restaurantsError, setRestaurantsError] = useState(null);

  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const abortRef = useRef(null);

  /* ─── LOAD RESTAURANTS ─── */
  const loadRestaurants = useCallback(async () => {
    setRestaurantsLoading(true);
    setRestaurantsError(null);
    try {
      setRestaurants(await getRestaurants());
    } catch (error) {
      console.error("Restaurant Load Error:", error);
      setRestaurantsError(error?.message || "Failed to load restaurants.");
    } finally {
      setRestaurantsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRestaurants();
  }, [loadRestaurants]);

  /* ─── LOAD EMPLOYEES + PROFILES + ATTENDANCE ─── */
  const loadRestaurantDetail = useCallback(async (restId, forMonth, forYear) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    setDetailLoading(true);
    setDetailError(null);

    try {
      const employeeData = await getEmployees({ signal });
      setEmployees(employeeData.filter((emp) => emp.activeRestId === restId));

      setProfiles(await getEmployeeProfiles(restId, { signal }));

      setAttendance(
        await getMonthlyAttendance({ restId, month: forMonth, year: forYear, signal })
      );
    } catch (error) {
      if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") return;
      console.error("Restaurant Detail Load Error:", error);
      setDetailError(error?.message || "Failed to load employee data for this restaurant.");
      setEmployees([]);
      setProfiles([]);
      setAttendance([]);
    } finally {
      if (!signal.aborted) setDetailLoading(false);
    }
  }, []);

  const handleRestaurantChange = (e) => {
    setSelectedRestId(Number(e.target.value));
    setCurrentIndex(0);
  };

  useEffect(() => {
    if (selectedRestId) loadRestaurantDetail(selectedRestId, month, year);
  }, [selectedRestId, month, year, loadRestaurantDetail]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const currentEmployee = employees[currentIndex];
  const currentProfile = profiles.find((p) => p.empId === currentEmployee?.empId);
  const currentAttendance = attendance.find((a) => a.empId === currentEmployee?.empId);

  const c = calcSalary(currentProfile, currentAttendance);
  const hasData = !!(currentProfile && currentAttendance);

  const efficiencyAccent =
    c.efficiencyPct === null
      ? "text-slate-400"
      : c.efficiencyPct >= 90
      ? "text-emerald-600"
      : c.efficiencyPct >= 70
      ? "text-orange-600"
      : "text-rose-600";

  const handlePrevious = () => currentIndex > 0 && setCurrentIndex((p) => p - 1);
  const handleNext = () =>
    currentIndex < employees.length - 1 && setCurrentIndex((p) => p + 1);

  const selectedRestaurantName = restaurants.find(
    (r) => r.restId === selectedRestId
  )?.restaurantName;

  const progressPct = employees.length ? ((currentIndex + 1) / employees.length) * 100 : 0;

  const overtimeSubtext = !currentProfile
    ? undefined
    : !c.isOT
    ? "Overtime not allowed"
    : `${minutesToHours(c.overtimeMinutes)} × ${formatINR(c.hourlyRate)}/hr`;

  /* ─── EXPORT ALL EMPLOYEES TO EXCEL ─── */
  const exportToExcel = () => {
    const rows = employees.map((emp, i) => {
      const p = profiles.find((x) => x.empId === emp.empId);
      const a = attendance.find((x) => x.empId === emp.empId);
      const e = calcSalary(p, a);
      return {
        "Sr No": i + 1,
        "Emp ID": emp.empId,
        "Employee Name": emp.empName,
        "Basic Salary": r2(e.basic),
        "Total Days": e.totalDays,
        "Full Days": a?.fullDays || 0,
        "Half Days": a?.halfDays || 0,
        "Quarter Days": a?.quarterDays || 0,
        "Attendance Credit": r2(e.attended),
        "Absent Days": r2(e.absent),
        "Suspense Days": a?.suspenseDays || 0,
        "Leave Credit": r2(e.leaveCredit),
        "Leave Without Pay": r2(e.leaveWithoutPay),
        "Paid Days": r2(e.paidDays),
        "Hours Worked": minutesToHours(e.actual),
        "Expected Hours": minutesToHours(e.expectedTotal),
        "Efficiency %": e.efficiencyPct === null ? "" : r2(e.efficiencyPct),
        "Overtime Allowed": e.isOT ? "Yes" : "No",
        "Overtime Hours": e.isOT ? minutesToHours(e.overtimeMinutes) : "0h 0m",
        "Hourly Rate": r2(e.hourlyRate),
        "Gross Salary": r2(e.gross),
        "Overtime Salary": r2(e.overtimeSalary),
        "Deductions": r2(e.deductions),
        "Net Salary": r2(e.net),
      };
    });

    const totalRow = { "Employee Name": "TOTAL" };
    ["Basic Salary", "Gross Salary", "Overtime Salary", "Deductions", "Net Salary"].forEach(
      (k) => (totalRow[k] = r2(rows.reduce((sum, row) => sum + row[k], 0)))
    );
    rows.push(totalRow);

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0]).map((k) => ({
      wch: k === "Employee Name" ? 24 : Math.max(k.length + 2, 12),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${MONTH_NAMES[month - 1]} ${year}`.slice(0, 31));
    XLSX.writeFile(
      wb,
      `Salary_${(selectedRestaurantName || "Restaurant").replace(/[^\w]+/g, "_")}_${MONTH_NAMES[month - 1]}_${year}.xlsx`
    );
  };

  const canExport = !!selectedRestId && !detailLoading && !detailError && employees.length > 0;

  return (
    <div className="min-h-screen bg-slate-50 p-3 md:p-6">
      <div className="mx-auto max-w-6xl space-y-4">

        {/* ── HEADER ── */}
        <div className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 md:flex-row md:items-center md:justify-between md:p-5">
          <div>
            <h1 className="text-xl font-bold text-slate-900 md:text-2xl">Salary Calculation</h1>
            <p className="mt-0.5 text-sm text-slate-500">
              Calculate employee salary based on monthly attendance
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 self-start md:self-auto">
            <div className="flex items-center gap-2 rounded-full bg-teal-50 px-3.5 py-1.5 text-sm font-medium text-teal-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {MONTH_NAMES[month - 1]} {year}
            </div>
            {currentAttendance && (
              <div className="flex items-center gap-2 rounded-full bg-slate-100 px-3.5 py-1.5 text-sm font-medium tabular-nums text-slate-700">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {minutesToHours(c.actual)} worked
              </div>
            )}
            {c.expectedTotal > 0 && (
              <div className="flex items-center gap-2 rounded-full bg-amber-50 px-3.5 py-1.5 text-sm font-medium tabular-nums text-amber-700">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m6 10V7M5 21h14a1 1 0 001-1V4a1 1 0 00-1-1H5a1 1 0 00-1 1v16a1 1 0 001 1z" />
                </svg>
                {minutesToHours(c.expectedPerDay)} × {c.workingDays}d = {minutesToHours(c.expectedTotal)} expected
              </div>
            )}
            <button
              onClick={exportToExcel}
              disabled={!canExport}
              title="Download all employees' salary details"
              className="flex items-center gap-2 rounded-full bg-emerald-600 px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
              </svg>
              Export Excel
            </button>
          </div>
        </div>

        {/* ── FILTERS ── */}
        <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 md:p-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Restaurant
              </label>
              {restaurantsError ? (
                <ErrorBanner message={restaurantsError} onRetry={loadRestaurants} />
              ) : (
                <select
                  value={selectedRestId}
                  onChange={handleRestaurantChange}
                  disabled={restaurantsLoading}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100 disabled:bg-slate-50 disabled:text-slate-400"
                >
                  <option value="">{restaurantsLoading ? "Loading…" : "Choose a restaurant"}</option>
                  {restaurants.map((r) => (
                    <option key={r.restId} value={r.restId}>
                      {r.restaurantName}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Month
              </label>
              <select
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              >
                {MONTH_NAMES.map((name, index) => (
                  <option key={index} value={index + 1}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Year
              </label>
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm tabular-nums outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              />
            </div>
          </div>
        </div>

        {/* ── DETAIL AREA ── */}
        {!selectedRestId ? (
          <EmptyState
            title="No restaurant selected"
            description="Choose a restaurant above to load employees and calculate salaries."
          />
        ) : detailLoading ? (
          <Skeleton />
        ) : detailError ? (
          <ErrorBanner
            message={detailError}
            onRetry={() => loadRestaurantDetail(selectedRestId, month, year)}
          />
        ) : employees.length === 0 ? (
          <EmptyState
            title="No employees found"
            description={`No active employees were found for ${selectedRestaurantName || "this restaurant"}.`}
          />
        ) : (
          currentEmployee && (
            <div className="space-y-4">

              {/* Employee identity + progress */}
              <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 md:p-5">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-600 text-sm font-semibold text-white">
                      {getInitials(currentEmployee.empName)}
                    </div>
                    <div className="min-w-0">
                      <h2 className="truncate text-lg font-bold text-slate-900">
                        {currentEmployee.empName}
                      </h2>
                      <p className="text-xs text-slate-500">Employee ID: {currentEmployee.empId}</p>
                    </div>
                  </div>
                  <div className="shrink-0 rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-semibold tabular-nums text-slate-700">
                    {currentIndex + 1} / {employees.length}
                  </div>
                </div>

                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-teal-600 transition-all duration-300"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>

              {/* KPI cards */}
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                <KpiCard label="Basic Salary" value={formatINR(hasData ? c.basic : 0)} />
                <KpiCard
                  label="Paid Days"
                  value={hasData ? f1(c.paidDays) : 0}
                  subtext={
                    hasData
                      ? `${f1(c.attended)} attended + ${f1(c.leaveCredit)} leave credit${
                          c.offPaidDays > 0 ? ` + ${c.offPaidDays} paid off` : ""
                        }`
                      : undefined
                  }
                />
                <KpiCard label="Gross Salary" value={formatINR(hasData ? c.gross : 0)} accent="emerald" />
                <KpiCard
                  label="Overtime Salary"
                  value={formatINR(hasData ? c.overtimeSalary : 0)}
                  accent="amber"
                  subtext={overtimeSubtext}
                />
                <KpiCard label="Deductions" value={formatINR(hasData ? c.deductions : 0)} accent="rose" />
                <KpiCard label="Net Salary" value={formatINR(hasData ? c.net : 0)} accent="teal" highlight />
              </div>

              {/* Attendance summary */}
              <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 md:p-5">
                <h3 className="mb-3 text-sm font-bold text-slate-900">Attendance summary</h3>
                <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
                  <AttendanceStat label="Full Days" value={currentAttendance?.fullDays || 0} accent="text-emerald-600" />
                  <AttendanceStat label="Half Days" value={currentAttendance?.halfDays || 0} accent="text-orange-600" />
                  <AttendanceStat label="Quarter Days" value={currentAttendance?.quarterDays || 0} accent="text-pink-600" />
                  <AttendanceStat
                    label="Attendance Credit"
                    value={f1(c.attended)}
                    accent="text-emerald-600"
                    subtext={`of ${c.workingDays} working days`}
                  />
                  <AttendanceStat
                    label="Absent Days"
                    value={f1(c.absent)}
                    accent="text-rose-600"
                    subtext={c.leaveCredit > 0 ? `${f1(c.leaveCredit)} covered by leave credit` : undefined}
                  />
                  <AttendanceStat label="Suspense Days" value={currentAttendance?.suspenseDays || 0} accent="text-purple-600" />
                  <AttendanceStat label="Working Hours" value={minutesToHours(c.actual)} accent="text-teal-600" />
                  <AttendanceStat label="Expected Hours/Day" value={minutesToHours(c.expectedPerDay)} accent="text-slate-700" />
                  <AttendanceStat
                    label="Overtime Hours"
                    value={c.isOT ? minutesToHours(c.overtimeMinutes) : "N/A"}
                    accent={c.isOT && c.overtimeMinutes > 0 ? "text-amber-600" : "text-slate-400"}
                    subtext={c.isOT ? undefined : "Not allowed"}
                  />
                  <AttendanceStat
                    label="Efficiency"
                    value={c.efficiencyPct !== null ? `${c.efficiencyPct.toFixed(1)}%` : "—"}
                    accent={efficiencyAccent}
                  />
                  <AttendanceStat
                    label="Leave Credit Used"
                    value={`${f1(c.leaveCredit)}/${c.leaveAllowed}`}
                    accent={c.leaveCredit > 0 ? "text-orange-600" : "text-slate-400"}
                  />
                  <AttendanceStat
                    label="Leave Without Pay"
                    value={f1(c.leaveWithoutPay)}
                    accent={c.leaveWithoutPay > 0 ? "text-rose-600" : "text-slate-400"}
                  />
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between gap-3">
                <button
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                  className="flex items-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous employee
                </button>

                <button
                  onClick={handleNext}
                  disabled={currentIndex === employees.length - 1}
                  className="flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next employee
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default SalaryCalculation;